import type { ReactNode } from "react";

/**
 * Подсветка синтаксиса для CodeBlock.
 *
 * Свой маленький токенизатор на регулярках вместо Prism/Shiki: в примерах
 * портала встречаются только cURL и JSON, а полноценная библиотека подсветки
 * весит больше, чем весь UI kit.
 *
 * Цвета — токены `--color-code-*`, они одинаковы в обеих темах: панель кода
 * всегда тёмная, как редактор, независимо от темы страницы.
 */
export type CodeLanguage = "bash" | "json" | "text";

type TokenType = "comment" | "string" | "key" | "keyword" | "variable" | "literal" | "punctuation";

const TOKEN_COLOR: Record<TokenType, string> = {
  comment: "var(--color-code-comment)",
  string: "var(--color-code-string)",
  key: "var(--color-code-key)",
  keyword: "var(--color-code-keyword)",
  variable: "var(--color-code-variable)",
  literal: "var(--color-code-literal)",
  punctuation: "var(--color-code-punctuation)",
};

const BASH_PATTERN =
  /(?<comment>#.*$)|(?<string>'[^']*'|"(?:\\.|[^"\\])*")|(?<variable>\$[A-Za-z_][A-Za-z0-9_]*)|(?<flag>(?:^|(?<=\s))-{1,2}[A-Za-z][\w-]*)|(?<keyword>\b(?:curl|GET|POST|PUT|PATCH|DELETE)\b)/gm;

const JSON_PATTERN =
  /(?<key>"(?:\\.|[^"\\])*"(?=\s*:))|(?<string>"(?:\\.|[^"\\])*")|(?<literal>-?\b\d+\.?\d*\b|\btrue\b|\bfalse\b|\bnull\b)|(?<punctuation>[{}[\],:])/g;

/** Язык из подписи блока: в разметке он человекочитаемый («cURL», «JSON»). */
export function codeLanguage(label?: string): CodeLanguage {
  const value = label?.trim().toLowerCase();
  if (value === "curl" || value === "bash" || value === "sh" || value === "shell") return "bash";
  if (value === "json") return "json";
  return "text";
}

function tokenize(line: string, pattern: RegExp, lineKey: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  pattern.lastIndex = 0;

  while ((match = pattern.exec(line))) {
    if (match.index > lastIndex) nodes.push(line.slice(lastIndex, match.index));

    const groups = match.groups ?? {};
    const group = Object.keys(groups).find((key) => groups[key] !== undefined);
    // Флаг (`-H`, `--data`) подсвечиваем как ключевое слово — отдельного цвета нет.
    const color = group === "flag" ? TOKEN_COLOR.keyword : group ? TOKEN_COLOR[group as TokenType] : undefined;
    const text = match[0];

    nodes.push(
      color ? (
        <span
          key={`${lineKey}-${match.index}`}
          style={{ color, fontStyle: group === "comment" ? "italic" : undefined }}
        >
          {text}
        </span>
      ) : (
        text
      ),
    );

    lastIndex = match.index + text.length;
    // Защита от зацикливания на совпадении нулевой длины.
    if (match.index === pattern.lastIndex) pattern.lastIndex++;
  }

  if (lastIndex < line.length) nodes.push(line.slice(lastIndex));
  return nodes;
}

export function highlightLine(line: string, language: CodeLanguage, lineKey: string): ReactNode {
  if (language === "bash") return tokenize(line, BASH_PATTERN, lineKey);
  if (language === "json") return tokenize(line, JSON_PATTERN, lineKey);
  return line;
}
